import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch, connect } from 'react-redux';
import cx from 'classnames';

import {
  LocationHelpers,
} from '../helpers';
import PageLoader from '../components/PageLoader';
import ThreadPanelWrapper from '../components/ThreadPanelWrapper';
import InboxThreadCompose from '../components/InboxThreadCompose';
import ThreadScrollWrapper from '../components/ThreadScrollWrapper';
import { fetchCompleteThreadView } from '../reducers/inboxReducer';
import InboxThreadSavedContentForm from '../components/InboxThreadSavedContentForm';
import InboxThreadHeader from '../components/InboxThreadHeader';
import * as ChatReducer from '../reducers/chatReducer';
import {
  getGroupUserDataLoaded,
} from '../selectors/chatSelectors';
import { getLoggedInUser } from '../selectors/userSelectors';
import ChatThreadCompose from '../components/ChatThreadCompose';
import ChatThreadHeader from '../components/ChatThreadHeader';
import ThreadModalWrapper from '../components/ThreadModalWrapper';
import { useThreadEvents, useUpdateRead } from '../hooks';
import threadService from '../services/threadService';
import ThreadFilterHeader from '../components/ThreadFilterHeader';

const ThreadView = (props) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const params = useParams();
  const isInboxThread = location.pathname.includes('inbox');
  const [loading, setLoading] = useState(true);
  useUpdateRead({ loading });
  const {
    isSelectionModeEnabled,
    pageLoading,
    profileOpen,
    userGroupDataLoaded,
    isCcr,
  } = props;
  const userId = Number(params.userId) || null;
  const groupId = Number(params.groupId) || null;

  const locationOptions = LocationHelpers.getInboxOptions(params, location);
  const { activePanel: locationActivePanel } = LocationHelpers.getQueryParams(location.search);
  if (location.state?.pageNo > -1) {
    locationOptions.pageNo = location.state.pageNo;
  }
  const query = useThreadEvents();

  useEffect(() => {
    setLoading(true);
    if (isInboxThread) {
      dispatch(threadService.util.invalidateTags([{ type: 'Thread', id: userId }]));
      dispatch(fetchCompleteThreadView({ locationOptions, userId, isCcr, locationActivePanel }));
    } else {
      fetchInitialChatThreadData();
    }
  }, [params.userId, params.groupId]);

  useEffect(() => {
    if (!pageLoading) {
      setLoading(pageLoading);
    }
  }, [pageLoading]);

  function fetchInitialChatThreadData() {
    const chatThreadOptions = {
      userId,
      groupId,
    };
    if (location.state?.pageNo > -1) {
      chatThreadOptions.pageNo = location.state.pageNo;
    }
    dispatch(ChatReducer.fetchCompleteChatThreadView(chatThreadOptions));
  }

  const appPanelClasses = cx('app-panels', {
    'app-panels--profile': profileOpen,
    'app-panels--hide-icons-desktop': !isInboxThread,
  });

  if (loading || !userGroupDataLoaded || query.isLoading || pageLoading) {
    return <PageLoader />;
  }

  const renderHeader = () => {
    if (isInboxThread) {
      return <InboxThreadHeader />;
    } return <ChatThreadHeader />;
  };

  const renderComposeArea = () => {
    if (isInboxThread) {
      return isSelectionModeEnabled ? <InboxThreadSavedContentForm /> : <InboxThreadCompose />;
    } return <ChatThreadCompose />;
  };

  return (
    <div className="app-page">
      <div className={appPanelClasses}>
        <div className="list-panel__wrapper">
          <div className="list-panel">
            <div className="convo__inner">
              {renderHeader()}
              {!!isInboxThread && (
              <ThreadFilterHeader />
              )}
              <ThreadScrollWrapper isInboxThread={isInboxThread} />
              {renderComposeArea()}
            </div>
          </div>
        </div>
        <ThreadPanelWrapper />
      </div>
      <ThreadModalWrapper />
    </div>
  );
};

ThreadView.propTypes = {
  isSelectionModeEnabled: PropTypes.bool.isRequired, // Governs state of selection mode on/off.
  pageLoading: PropTypes.bool,
  profileOpen: PropTypes.bool,
  userGroupDataLoaded: PropTypes.bool,
  isCcr: PropTypes.bool,
};

const mapStateToProps = (state, props) => {
  const {
    inbox,
    thread,
    chat,
  } = state;
  return {
    userGroupDataLoaded: getGroupUserDataLoaded(state, props),
    isSelectionModeEnabled: state.savedContent.isSelectionModeEnabled,
    pageLoading: props.location.pathname.includes('inbox') ? inbox.pageLoading : chat.pageLoading,
    profileOpen: thread.profileOpen,
    isCcr: getLoggedInUser(state)?.isCcr,
    organizationId: state.auth.currentOrg,
  };
};

export default connect(mapStateToProps)(ThreadView);
